
import {
  defineComponent, PropType, Ref, ref, watch, onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InlineMessage from 'primevue/inlinemessage';

import { FieldConfig, ParserFieldObject, Property } from '@/types';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import { getVariablesWithCollectionContext, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';

export default defineComponent({
  name: 'InputVariables',
  components: {
    BaseInputText,
    InlineMessage,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);
    const inputValue: Ref<string> = ref(properties.value[0].value ?? props.configs.options.defaultValue);
    const enrichedVariables = ref(props.configs.options.variablesList || []);
    const validationError: Ref<string | null> = ref(null);
    const validationErrorParams: Ref<Record<string, string | number>> = ref({});

    const validateContent = () => {
      const trimmed = (inputValue.value || '').replace(/<[^>]*>/g, '').trim();

      if (props.configs.options?.required && !trimmed) {
        validationError.value = props.configs.options.requiredErrorKey || null;
        validationErrorParams.value = {};
        return;
      }

      if (props.configs.options?.validator) {
        const result = props.configs.options.validator(inputValue.value);
        validationError.value = result.isValid ? null : (result.errorKey || null);
        validationErrorParams.value = result.errorParams || {};
        return;
      }

      validationError.value = null;
      validationErrorParams.value = {};
    };

    const handleValueChange = () => {
      validateContent();

      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = inputValue.value;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    watch(() => properties.value[0].value, (values, oldValues) => {
      if (values !== oldValues) {
        inputValue.value = properties.value[0].value;
      }
    }, { immediate: true });

    onMounted(async () => {
      validateContent();

      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties to base variables (for use outside loops)
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables from custom events
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    return {
      t,
      inputValue,
      enrichedVariables,
      validationError,
      validationErrorParams,
      handleValueChange,
    };
  },
});
